import {
  resolveAuthenticatedSupabaseUser,
  withAuthReturnPath,
} from "./onboarding-auth.js";
import { getSupabaseBrowserClient } from "./supabase-browser-client.js";

function canUseWindow() {
  return typeof window !== "undefined";
}

function readErrorStatus(error) {
  const status = Number(error?.status ?? error?.statusCode ?? error?.response?.status ?? 0);
  return Number.isFinite(status) ? status : 0;
}

function readErrorMessage(error) {
  if (error instanceof Error) {
    return String(error.message || "").trim();
  }
  if (error && typeof error === "object") {
    return String(error.message || error.detail || "").trim();
  }
  return String(error || "").trim();
}

export function isAuthenticationRequiredError(error) {
  if (readErrorStatus(error) === 401) {
    return true;
  }
  const message = readErrorMessage(error);
  return /^(authentication required|missing access token)$/i.test(message)
    || /auth session missing/i.test(message)
    || /(?:invalid|expired|missing|not found|already used).{0,80}refresh token/i.test(message)
    || /refresh token.{0,80}(?:invalid|expired|missing|not found|already used)/i.test(message);
}

export function buildAuthReturnPath(locationLike = null) {
  const locationSource = locationLike || (canUseWindow() ? window.location : null);
  const pathname = String(locationSource?.pathname || "").trim();
  if (!pathname || !pathname.startsWith("/") || pathname.startsWith("//")) {
    return "";
  }
  return `${pathname}${locationSource?.search || ""}`;
}

export function buildAuthRequiredRedirectPath(locationLike = null) {
  return withAuthReturnPath("/auth", buildAuthReturnPath(locationLike));
}

export function redirectToAuth(navigate, { location = null, replace = true } = {}) {
  if (typeof navigate !== "function") {
    return false;
  }
  navigate(buildAuthRequiredRedirectPath(location), { replace });
  return true;
}

export function redirectToAuthIfRequired(error, options = {}) {
  if (!isAuthenticationRequiredError(error)) {
    return false;
  }
  return redirectToAuth(options.navigate, options);
}

async function loadAuthenticatedRouteUser() {
  const client = getSupabaseBrowserClient();
  if (!client) {
    throw new Error("Supabase is not configured.");
  }
  const {
    data: { session },
    error,
  } = await client.auth.getSession();
  if (error) {
    throw error;
  }
  return resolveAuthenticatedSupabaseUser(client, session);
}

export async function redirectToAuthIfSignedOut(options = {}) {
  const authenticatedUserLoader = typeof options.resolveAuthenticatedUser === "function"
    ? options.resolveAuthenticatedUser
    : loadAuthenticatedRouteUser;
  try {
    const user = await authenticatedUserLoader();
    return user ? false : redirectToAuth(options.navigate, options);
  } catch (error) {
    if (!isAuthenticationRequiredError(error)) {
      return false;
    }
    return redirectToAuth(options.navigate, options);
  }
}
